import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, Clock, X, Filter } from "lucide-react";
import { api } from "@/lib/api";
import SEO, { collectionSchema } from "@/components/SEO";

const FALLBACK_IMG =
  "https://images.unsplash.com/photo-1562237553-36ad661d6f2c?w=1600&q=80";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const estimateReadMinutes = (content = "") =>
  Math.max(2, Math.round(content.split(/\s+/).length / 180));

const Chip = ({ label, active, onClick, testId, accent = "navy" }) => {
  const base =
    "inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-archivo uppercase tracking-widest border transition-colors";
  const styles = active
    ? accent === "copper"
      ? "bg-bb-copper text-white border-bb-copper"
      : "bg-bb-navy text-white border-bb-navy"
    : "bg-white text-bb-navy border-border hover:border-bb-navy";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${base} ${styles}`}
      data-testid={testId}
      data-active={active}
    >
      {label}
      {active && <X size={11} strokeWidth={2.5} />}
    </button>
  );
};

const ArticleCard = ({ article, regionLabel, commodityLabel }) => {
  const minutes = estimateReadMinutes(article.content);
  return (
    <article
      className="bb-card group flex flex-col"
      data-testid={`news-card-${article.slug}`}
    >
      <Link to={`/news/${article.slug}`} className="block overflow-hidden border-b border-border">
        <img
          src={article.featuredImage || FALLBACK_IMG}
          alt={article.title}
          className="bb-img w-full aspect-[16/10] object-cover transition-transform duration-500 group-hover:scale-[1.03]"
        />
      </Link>
      <div className="p-6 flex flex-col gap-3 flex-1">
        <div className="flex items-center justify-between">
          <span className="bb-overline text-bb-copper">{article.category}</span>
          <span className="text-[11px] font-mono text-bb-slate">
            {formatDate(article.publishedDate)}
          </span>
        </div>
        <h3 className="font-archivo text-lg lg:text-xl text-bb-navy font-semibold tracking-tight leading-snug">
          <Link to={`/news/${article.slug}`} className="bb-link">
            {article.title}
          </Link>
        </h3>
        <p className="text-sm text-bb-slate leading-relaxed">
          {article.excerpt}
        </p>
        <div className="mt-auto pt-4 border-t border-border flex items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-mono text-bb-slate">
            {regionLabel && <span>{regionLabel}</span>}
            {commodityLabel && (
              <>
                <span className="opacity-40">·</span>
                <span>{commodityLabel}</span>
              </>
            )}
          </div>
          <span className="inline-flex items-center gap-1 text-[11px] font-mono text-bb-slate whitespace-nowrap">
            <Clock size={11} /> {minutes} min
          </span>
        </div>
      </div>
    </article>
  );
};

export default function NewsListPage() {
  const [articles, setArticles] = useState([]);
  const [regions, setRegions] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category") || null;
  const region = searchParams.get("region") || null;
  const commodity = searchParams.get("commodity") || null;

  const updateParam = (key, nextValue) => {
    const next = new URLSearchParams(searchParams);
    if (!nextValue || next.get(key) === nextValue) next.delete(key);
    else next.set(key, nextValue);
    setSearchParams(next, { replace: false });
  };

  const setCategory = (v) => updateParam("category", v);
  const setRegion = (v) => updateParam("region", v);
  const setCommodity = (v) => updateParam("commodity", v);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      api.get("/articles"),
      api.get("/regions"),
      api.get("/commodities"),
    ])
      .then(([a, r, c]) => {
        if (!mounted) return;
        if (Array.isArray(a.data)) setArticles(a.data);
        if (Array.isArray(r.data)) setRegions(r.data);
        if (Array.isArray(c.data)) setCommodities(c.data);
      })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const regionMap = useMemo(
    () => Object.fromEntries(regions.map((r) => [r.id, r])),
    [regions],
  );
  const commodityMap = useMemo(
    () => Object.fromEntries(commodities.map((c) => [c.id, c])),
    [commodities],
  );

  const categories = useMemo(() => {
    const set = new Set();
    articles.forEach((a) => a.category && set.add(a.category));
    return Array.from(set).sort();
  }, [articles]);

  const filtered = useMemo(
    () =>
      articles.filter((a) => {
        if (category && a.category !== category) return false;
        if (region && a.region !== region) return false;
        if (commodity && a.commodity !== commodity) return false;
        return true;
      }),
    [articles, category, region, commodity],
  );

  const activeFilterCount =
    (category ? 1 : 0) + (region ? 1 : 0) + (commodity ? 1 : 0);

  const clearAll = () => {
    setSearchParams(new URLSearchParams(), { replace: false });
  };

  return (
    <div data-testid="news-list-page">
      <SEO
        title="News"
        description="A live editorial feed of Canadian mining sector developments — breaking news, regulatory filings, project updates, and capital markets moves."
        jsonLd={collectionSchema("News", "Canadian mining news feed", "/news")}
      />
      {/* Page header */}
      <section className="bg-white border-b border-border">
        <div className="bb-container py-12 lg:py-16">
          <div className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <div className="bb-overline text-bb-copper">
                The Brief · Editorial Feed
              </div>
              <h1
                className="bb-headline mt-4 text-4xl sm:text-5xl lg:text-6xl text-bb-navy"
                data-testid="news-page-headline"
              >
                News
              </h1>
              <p className="mt-5 text-base lg:text-lg text-bb-slate max-w-2xl leading-relaxed">
                A live editorial feed of Canadian mining sector developments —
                breaking news, regulatory filings, project updates, and capital
                markets moves, organised by editorial desk, region, and
                commodity.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="bb-overline text-bb-slate">
                {filtered.length} {filtered.length === 1 ? "story" : "stories"}
              </span>
              {activeFilterCount > 0 && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="bb-btn-outline inline-flex items-center gap-2 px-4 py-2 text-xs"
                  data-testid="news-clear-filters"
                >
                  <X size={14} strokeWidth={2.4} />
                  Clear filters ({activeFilterCount})
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Filter chips */}
      <section
        className="bg-bb-offwhite border-b border-border sticky top-16 z-30"
        data-testid="news-filter-bar"
      >
        <div className="bb-container py-5 space-y-3">
          <div className="flex items-start gap-4">
            <div className="flex items-center gap-2 min-w-[110px] pt-1.5">
              <Filter size={13} className="text-bb-slate" />
              <span className="bb-overline text-bb-slate">Category</span>
            </div>
            <div
              className="flex flex-wrap gap-2"
              data-testid="news-filter-category"
            >
              {categories.map((c) => (
                <Chip
                  key={c}
                  label={c}
                  active={category === c}
                  onClick={() => setCategory(category === c ? null : c)}
                  testId={`chip-category-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  accent="copper"
                />
              ))}
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="flex items-center gap-2 min-w-[110px] pt-1.5">
              <span className="bb-overline text-bb-slate">Region</span>
            </div>
            <div
              className="flex flex-wrap gap-2"
              data-testid="news-filter-region"
            >
              {regions.map((r) => (
                <Chip
                  key={r.id}
                  label={r.name}
                  active={region === r.id}
                  onClick={() => setRegion(region === r.id ? null : r.id)}
                  testId={`chip-region-${r.slug}`}
                />
              ))}
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="flex items-center gap-2 min-w-[110px] pt-1.5">
              <span className="bb-overline text-bb-slate">Commodity</span>
            </div>
            <div
              className="flex flex-wrap gap-2"
              data-testid="news-filter-commodity"
            >
              {commodities.map((c) => (
                <Chip
                  key={c.id}
                  label={c.name}
                  active={commodity === c.id}
                  onClick={() =>
                    setCommodity(commodity === c.id ? null : c.id)
                  }
                  testId={`chip-commodity-${c.slug}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Results grid */}
      <section className="py-12 lg:py-16">
        <div className="bb-container">
          {loading ? (
            <div
              className="text-center py-20 bb-overline text-bb-slate"
              data-testid="news-loading"
            >
              Loading the brief…
            </div>
          ) : filtered.length === 0 ? (
            <div
              className="border border-border bg-white p-12 text-center"
              data-testid="news-empty-state"
            >
              <div className="bb-overline text-bb-copper">No results</div>
              <h3 className="font-archivo text-2xl text-bb-navy mt-3 font-semibold">
                Nothing matches those filters yet.
              </h3>
              <p className="text-sm text-bb-slate mt-3 max-w-md mx-auto">
                Try removing a filter, or browse the full feed. Our editors
                publish to the desk multiple times each day.
              </p>
              <button
                type="button"
                onClick={clearAll}
                className="bb-btn-primary inline-flex items-center justify-center gap-2 px-5 py-3 text-sm mt-6"
                data-testid="news-empty-clear"
              >
                Clear all filters
                <ArrowUpRight size={14} strokeWidth={2.4} />
              </button>
            </div>
          ) : (
            <div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
              data-testid="news-grid"
            >
              {filtered.map((a) => (
                <ArticleCard
                  key={a.id}
                  article={a}
                  regionLabel={regionMap[a.region]?.name || null}
                  commodityLabel={commodityMap[a.commodity]?.name || null}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
